import { setManifest } from '@angular-architects/module-federation';

import { environment } from './environments/environment';

/**
 * Warm up DNS lookup and the TLS handshake for the API and identity origins while the
 * bootstrap chunk is still downloading. On slow merchant networks this removes a full
 * round trip from both the Keycloak login redirect and the first API call.
 *
 * The API is reached over CORS without credentials, so it needs an anonymous preconnect.
 * Keycloak is first reached through a top-level navigation, which needs a plain one.
 */
function preconnect(url: string | undefined, anonymous: boolean): void {
  if (!url) {
    return;
  }
  let origin: string;
  try {
    origin = new URL(url, window.location.href).origin;
  } catch {
    return; // malformed config entry, nothing to warm up
  }
  if (origin === window.location.origin) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  if (anonymous) {
    link.crossOrigin = 'anonymous';
  }
  document.head.appendChild(link);
}

// to parallelize the fetch of version and config, and to avoid nested promises
Promise.all([
  fetch('/assets/version.json').then((res) => res.json()), // Fetch version information
  fetch('/assets/config.json').then((res) => res.json()), // Fetch configuration information
])
  .then(([version, config]) => {
    const storedAppVersion = localStorage.getItem('insights_frontend_app_version');
    if (storedAppVersion && storedAppVersion !== version.version) {
      localStorage.setItem('insights_frontend_app_version', version.version);
      location.reload();
      return;
    }
    localStorage.setItem('insights_frontend_app_version', version.version);

    preconnect(config.keycloakURL, false);
    preconnect(config.baseUrl, true);

    console.log('██╗███╗   ██╗███████╗██╗ ██████╗ ██╗  ██╗████████╗███████╗');
    console.log('██║████╗  ██║██╔════╝██║██╔════╝ ██║  ██║╚══██╔══╝██╔════╝');
    console.log('██║██╔██╗ ██║███████╗██║██║  ███╗███████║   ██║   ███████╗');
    console.log('██║██║╚██╗██║╚════██║██║██║   ██║██╔══██║   ██║   ╚════██║');
    console.log('██║██║ ╚████║███████║██║╚██████╔╝██║  ██║   ██║   ███████║');
    console.log('╚═╝╚═╝  ╚═══╝╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝');
    console.log(`Version: ${version.version}`);
    console.log(`Build: ${environment.production ? 'Production' : 'Development'}`);

    // Skip eager remote entry downloads: routes load each remote on demand via
    // loadRemoteModule(), so fetching every remoteEntry.mjs here only delays bootstrap.
    setManifest(config.remoteApplications, true)
      .catch((err) => {
        console.error('Error setting manifest:', err);
      })
      .then(() => {
        import('./bootstrap').then((f) => f.bootstrap(config, version));
      });
  })
  .catch((err) => {
    console.error('Error fetching version or config:', err);
  });
